/**
 * EN-1325: coalesces realtime broadcasts that each trigger a refetch of one record.
 *
 * Every open tab receives every "patient" broadcast, and one record change can emit several
 * in quick succession. Without this, each broadcast refetched immediately in every tab.
 *
 * - Trailing debounce per id: a burst of broadcasts for the same id runs once, `delayMs`
 *   after the last one — but never later than `maxWaitMs` after the first, so a record that
 *   keeps broadcasting still refreshes.
 * - At most one run per id in flight; broadcasts that land during it queue exactly one follow-up.
 * - With `isHidden`, broadcasts received while hidden only mark the id dirty; `flushDirty()`
 *   (call on becoming visible) schedules each dirty id once.
 */
type Options = {
    delayMs: number;
    maxWaitMs?: number;
    run: (id: string) => Promise<unknown>;
    isHidden?: () => boolean;
};

export type BroadcastCoalescer = {
    schedule: (id: string) => void;
    flushDirty: () => void;
    dispose: () => void;
};

export function createBroadcastCoalescer({ delayMs, maxWaitMs, run, isHidden }: Options): BroadcastCoalescer {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const burstStartedAt = new Map<string, number>();
    const inFlight = new Set<string>();
    const rerun = new Set<string>();
    const dirty = new Set<string>();
    let disposed = false;

    const execute = (id: string) => {
        timers.delete(id);
        burstStartedAt.delete(id);
        if (disposed) return;
        if (inFlight.has(id)) {
            rerun.add(id);
            return;
        }
        inFlight.add(id);
        let pending: Promise<unknown>;
        try {
            pending = Promise.resolve(run(id));
        } catch (error) {
            pending = Promise.reject(error);
        }
        pending
            .catch(() => undefined) // the run owns its own error handling; a failure must not wedge the id
            .finally(() => {
                inFlight.delete(id);
                if (rerun.delete(id)) schedule(id);
            });
    };

    const schedule = (id: string) => {
        if (disposed) return;
        if (isHidden?.()) {
            dirty.add(id);
            return;
        }
        const now = Date.now();
        if (!burstStartedAt.has(id)) burstStartedAt.set(id, now);
        const untilMaxWait = maxWaitMs === undefined ? delayMs : Math.max(0, burstStartedAt.get(id)! + maxWaitMs - now);
        const existing = timers.get(id);
        if (existing) clearTimeout(existing);
        timers.set(
            id,
            setTimeout(() => execute(id), Math.min(delayMs, untilMaxWait)),
        );
    };

    const flushDirty = () => {
        const ids = Array.from(dirty);
        dirty.clear();
        ids.forEach(schedule);
    };

    const dispose = () => {
        disposed = true;
        timers.forEach(clearTimeout);
        timers.clear();
        burstStartedAt.clear();
        dirty.clear();
        rerun.clear();
    };

    return { schedule, flushDirty, dispose };
}
